import React, { useEffect, useState } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import moment from "moment";
import DateTime from "react-datetime";
import ReactJoyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

import axiosBackend from "../../../../core/api/backend";
import Loader from "../../../../components/Loader";
import ErrorPage from "../../../error";
import API_STATES from "../../../../api-states";
import steps from "./help-steps";
import MarkIntroductionTourDone from "../../../../core/onboarding/mark-introduction-tour-done";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

export const ResourceUsageIntroductionIdentifier = "resources/resource-usage-introduction";

function ResourceUsage() {
    const params = useParams();
    const {
        shouldRun,
    } = useLoaderData();

    const [status, setStatus] = useState(API_STATES.LOADING);
    const [error, setError] = useState(null);
    const [originalError, setOriginalError] = useState(null);


    // Time period state
    const [days, setDays] = useState(7);
    const [startDate, setStartDate] = useState(
        new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
    );
    const [endDate, setEndDate] = useState(
        new Date(new Date().getTime() + 1 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
    );

    // Usage data state
    const [usageData, setUsageData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [usageByResource, setUsageByResource] = useState([]);
    const [operationTrendChartType, setOperationTrendChartType] = useState('line');
    const [usageByResourceChartType, setUsageByResourceChartType] = useState('bar');
    const [sortColumn, setSortColumn] = useState('totalCount');
    const [sortDirection, setSortDirection] = useState('desc');

    // Help tour state
    const [showTour, setShowTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        if (shouldRun) {
            setShowTour(true);
        }
    }, [shouldRun]);

    // Change statistics filter (similar to datalog)
    const changeStatisticsFilter = (numberOfDays) => {
        setDays(numberOfDays);
        setStartDate(
            new Date(new Date().getTime() - numberOfDays * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0]
        );
        setEndDate(
            new Date(new Date().getTime() + 1 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0]
        );
    };

    // Load usage data
    useEffect(() => {
        const loadUsageData = async () => {
            try {
                setStatus(API_STATES.LOADING);
                setError(null);
                setOriginalError(null);

                let data;

                // Load from real backend API
                const response = await axiosBackend.post(
                    `/service/resources/${params.appUUID}/history/usage`,
                    {
                        startDate: moment(startDate).startOf("day").toISOString(),
                        endDate: moment(endDate).endOf("day").toISOString(),
                    }
                );

                if (!response.data || !response.data.results) {
                    throw new Error("Invalid response format from API");
                }

                data = response.data.results;

                if (data && Array.isArray(data)) {
                    setUsageData(data);

                    // Process chart data
                    processChartData(data);

                    // Group usage by resource
                    const byResource = groupUsageByResource(data);
                    setUsageByResource(byResource);

                    setStatus(API_STATES.LOADED);
                }
            } catch (err) {
                console.error("Error loading usage data:", err);
                setError(err.response?.data?.messages || err.message);
                setOriginalError(err);
                setStatus(API_STATES.ERROR);
            }
        };

        loadUsageData();
    }, [params.appUUID, startDate, endDate]);

    // Process usage data for charts
    const processChartData = (data) => {
        if (!data) return;

        // Group data by date
        const dateGrouped = {};
        const operationTypes = { write: 0, update: 0, delete: 0 };

        data.forEach((item) => {
            // Sum up operations
            operationTypes.write += item.writeCount || 0;
            operationTypes.update += item.updateCount || 0;
            operationTypes.delete += item.deleteCount || 0;

            // Group by date
            const date = moment(item.timestamp).format("YYYY-MM-DD");
            if (!dateGrouped[date]) {
                dateGrouped[date] = {
                    write: 0,
                    update: 0,
                    delete: 0,
                    total: 0,
                };
            }

            dateGrouped[date].write += item.writeCount || 0;
            dateGrouped[date].update += item.updateCount || 0;
            dateGrouped[date].delete += item.deleteCount || 0;
            dateGrouped[date].total +=
                (item.writeCount || 0) +
                (item.updateCount || 0) +
                (item.deleteCount || 0);
        });

        const sortedDates = Object.keys(dateGrouped).sort();

        // Line chart data
        const lineChartData = {
            labels: sortedDates,
            datasets: [
                {
                    label: "Create Records",
                    data: sortedDates.map((date) => dateGrouped[date].write),
                    borderColor: "#2ecc71",
                    backgroundColor: "rgba(46, 204, 113, 0.1)",
                    borderWidth: 2,
                    tension: 0.3,
                    datalabels: {
                        display: (ctx) => {
                            const v = ctx.dataset.data[ctx.dataIndex];
                            return v !== 0 && v != null;
                        },
                        align: 'top',
                        anchor: 'end',
                        offset: 8,
                        font: {
                            size: 10,
                            weight: 'bold',
                        },
                        color: '#2ecc71',
                    },
                },
                {
                    label: "Update Records",
                    data: sortedDates.map((date) => dateGrouped[date].update),
                    borderColor: "#f39c12",
                    backgroundColor: "rgba(243, 156, 18, 0.1)",
                    borderWidth: 2,
                    tension: 0.3,
                    datalabels: {
                        display: (ctx) => {
                            const v = ctx.dataset.data[ctx.dataIndex];
                            return v !== 0 && v != null;
                        },
                        align: 'top',
                        anchor: 'end',
                        offset: 8,
                        font: {
                            size: 10,
                            weight: 'bold',
                        },
                        color: '#f39c12',
                    },
                },
                {
                    label: "Delete Records",
                    data: sortedDates.map((date) => dateGrouped[date].delete),
                    borderColor: "#e74c3c",
                    backgroundColor: "rgba(231, 76, 60, 0.1)",
                    borderWidth: 2,
                    tension: 0.3,
                    datalabels: {
                        display: (ctx) => {
                            const v = ctx.dataset.data[ctx.dataIndex];
                            return v !== 0 && v != null;
                        },
                        align: 'top',
                        anchor: 'end',
                        offset: 8,
                        font: {
                            size: 10,
                            weight: 'bold',
                        },
                        color: '#e74c3c',
                    },
                },
            ],
        };

        setChartData({
            lineChart: lineChartData,
            operationTypes,
        });
    };

    // Group usage data by resource
    const groupUsageByResource = (data) => {
        const grouped = {};

        data.forEach((item) => {
            if (!grouped[item.resourceName]) {
                grouped[item.resourceName] = {
                    resourceName: item.resourceName,
                    writeCount: 0,
                    updateCount: 0,
                    deleteCount: 0,
                    totalCount: 0,
                };
            }

            grouped[item.resourceName].writeCount += item.writeCount || 0;
            grouped[item.resourceName].updateCount += item.updateCount || 0;
            grouped[item.resourceName].deleteCount += item.deleteCount || 0;
            grouped[item.resourceName].totalCount +=
                (item.writeCount || 0) +
                (item.updateCount || 0) +
                (item.deleteCount || 0);
        });

        return Object.values(grouped).sort(
            (a, b) => b.totalCount - a.totalCount
        );
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: window.innerWidth < 768 ? 1.5 : 2.5,
        layout: {
            padding: {
                top: 20,
                bottom: 20,
            },
        },
        plugins: {
            legend: {
                position: "top",
            },
            datalabels: {
                display: true,
            },
        },
        scales: {
            x: {
                ticks: {
                    padding: 10,
                    maxRotation: 45,
                    minRotation: 0,
                    autoSkip: true,
                },
            },
            y: {
                beginAtZero: true,
                grace: '10%',
                ticks: {
                    maxTicksLimit: 6,
                    callback: function (value) {
                        return value.toLocaleString();
                    },
                },
            },
        },
    };

    const resourceBarChartData = {
        labels: usageByResource.map((r) => r.resourceName),
        datasets: [
            {
                label: "Create Records",
                data: usageByResource.map((r) => r.writeCount),
                backgroundColor: "#2ecc71",
                borderColor: "#2ecc71",
                borderWidth: 2,
                tension: 0.3,
                datalabels: {
                    display: (ctx) => {
                        const v = ctx.dataset.data[ctx.dataIndex];
                        return v !== 0 && v != null;
                    },
                    align: 'top',
                    anchor: 'end',
                    offset: 8,
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    color: '#2ecc71',
                },
            },
            {
                label: "Update Records",
                data: usageByResource.map((r) => r.updateCount),
                backgroundColor: "#f39c12",
                borderColor: "#f39c12",
                borderWidth: 2,
                tension: 0.3,
                datalabels: {
                    display: (ctx) => {
                        const v = ctx.dataset.data[ctx.dataIndex];
                        return v !== 0 && v != null;
                    },
                    align: 'top',
                    anchor: 'end',
                    offset: 8,
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    color: '#f39c12',
                },
            },
            {
                label: "Delete Records",
                data: usageByResource.map((r) => r.deleteCount),
                backgroundColor: "#e74c3c",
                borderColor: "#e74c3c",
                borderWidth: 2,
                tension: 0.3,
                datalabels: {
                    display: (ctx) => {
                        const v = ctx.dataset.data[ctx.dataIndex];
                        return v !== 0 && v != null;
                    },
                    align: 'top',
                    anchor: 'end',
                    offset: 8,
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    color: '#e74c3c',
                },
            },
        ],
    };

    const toggleHelp = () => {
        setShowTour(true);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            // Toggle direction if clicking same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new column and default to descending
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const getSortedResources = () => {
        const sorted = [...usageByResource].sort((a, b) => {
            let aVal = a[sortColumn];
            let bVal = b[sortColumn];

            // For string comparisons (resource name)
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
                return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }

            // For numeric comparisons
            if (sortDirection === 'asc') {
                return aVal - bVal;
            } else {
                return bVal - aVal;
            }
        });
        return sorted;
    };

    const getSortIndicator = (column) => {
        if (sortColumn !== column) return null;
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const handleJoyrideCallback = (data) => {
        if ([STATUS.FINISHED, STATUS.SKIPPED, STATUS.CLOSE].includes(data.status) || ACTIONS.CLOSE === data.action) {
            setShowTour(false);
            setStepIndex(0);

            if (shouldRun) {
                MarkIntroductionTourDone({ identifier: ResourceUsageIntroductionIdentifier });
            }
        } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(data.type)) {
            setStepIndex(data.index + (data.action === ACTIONS.PREV ? -1 : 1));
        }
    };

    return (
        <div className="col-sm-12">
            <ReactJoyride
                callback={handleJoyrideCallback}
                run={showTour}
                stepIndex={stepIndex}
                steps={steps}
                continuous={true}
                showSkipButton={true}
                showProgress={true}
                styles={{
                    options: {
                        zIndex: 10000,
                    },
                }}
                disableScrollParentFix={true}
            />

            {status === API_STATES.ERROR &&
                <ErrorPage
                    hideSidebar={true}
                    message={error}
                    title={originalError?.response?.status === 403 ? "Permission Denied" : undefined}
                />}

            {/* Help Button */}
            <div className="col-lg-12 pb-2">
                <button className="btn btn-gray" onClick={toggleHelp}>
                    <span className="far fa-question-circle fa-lg"></span>
                </button>
            </div>

            {/* Time Period Selector */}
            <div className="row mb-3">
                <div className="col-sm-12">
                    <div className="panel p-3">
                        <div className="row">
                            <div className="col-lg-12 mb-2">
                                <h2 className="mt-0">Resource Usage Analytics</h2>
                                <p>
                                    Monitor your resource records including creates, updates, and deletes over time
                                </p>
                            </div>
                        </div>
                        <div className="row align-items-center">
                            <div className="col-12 col-md-4 mb-3 mb-md-0" id="usage-date-range">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem' }}>Date Range</small>
                                <div className="row">
                                    <div className="col-12 col-sm-6">
                                        <DateTime
                                            timeFormat={false}
                                            value={startDate}
                                            dateFormat="YYYY-MM-DD"
                                            className="text-small"
                                            onChange={(date) =>
                                                setStartDate(date.toISOString().split("T")[0])
                                            }
                                            disabled={status === API_STATES.LOADING}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 mt-2 mt-sm-0">
                                        <DateTime
                                            timeFormat={false}
                                            value={endDate}
                                            dateFormat="YYYY-MM-DD"
                                            className="text-small"
                                            onChange={(date) =>
                                                setEndDate(date.toISOString().split("T")[0])
                                            }
                                            disabled={status === API_STATES.LOADING}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-8">
                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem' }}>Quick Select</small>
                                <div className="d-flex flex-wrap" style={{ gap: '6px' }} id="usage-quick-select">
                                    <button
                                        onClick={() => changeStatisticsFilter(1)}
                                        className={`btn btn-xs ${days === 1 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        1D
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(7)}
                                        className={`btn btn-xs ${days === 7 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        7D
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(30)}
                                        className={`btn btn-xs ${days === 30 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        1M
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(90)}
                                        className={`btn btn-xs ${days === 90 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        3M
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(31 * 6)}
                                        className={`btn btn-xs ${days === 31 * 6 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        6M
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(moment().dayOfYear())}
                                        className={`btn btn-xs ${days === moment().dayOfYear() ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        YTD
                                    </button>
                                    <button
                                        onClick={() => changeStatisticsFilter(365)}
                                        className={`btn btn-xs ${days === 365 ? "btn-primary" : "btn-white"}`}
                                        style={{ flex: '1 1 auto', minWidth: '50px' }}
                                        disabled={status === API_STATES.LOADING}
                                    >
                                        1Y
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {status === API_STATES.LOADING && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <Loader />
                </div>
            )}

            {status === API_STATES.LOADED && (
                <>
                    {/* Statistics Cards */}
                    {chartData && (
                        <div className="row mb-3" id="usage-statistics-cards">
                            <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-0">
                                <div className="panel p-2">
                                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}><i className="fas fa-pen"></i> CREATE RECORDS</h6>
                                    <p className="h5 mb-0 font-weight-bold">{chartData.operationTypes.write.toLocaleString()}</p>
                                </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-0">
                                <div className="panel p-2">
                                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}><i className="fas fa-sync"></i> UPDATE RECORDS</h6>
                                    <p className="h5 mb-0 font-weight-bold">{chartData.operationTypes.update.toLocaleString()}</p>
                                </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4">
                                <div className="panel p-2">
                                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}><i className="fas fa-trash"></i> DELETE RECORDS</h6>
                                    <p className="h5 mb-0 font-weight-bold">{chartData.operationTypes.delete.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Charts Section */}
                    {chartData && (
                        <>
                            {/* Row: Left Column - Table, Right Column - Bar Chart & Line Chart */}
                            <div className="row">
                                {/* Left Column: Resource-wise Usage Breakdown */}
                                {usageByResource.length > 0 && (
                                    <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                                        <div class="ibox-content mt-0 no-top-border pb-0">
                                            <div className="row">
                                                <div className="col">
                                                    <h4>Resource Usage Breakdown</h4>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="panel p-2" id="usage-resource-breakdown" style={{ overflowX: 'auto' }}>
                                            <div className="table-responsive" style={{ minWidth: '100%', overflowX: 'auto' }}>
                                                <table className="table table-hover mb-0" style={{ minWidth: '500px' }}>
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('resourceName')}>Resource{getSortIndicator('resourceName')}</th>

                                                            <th className="text-right" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('writeCount')}>Creates{getSortIndicator('writeCount')}</th>
                                                            <th className="text-right d-sm-table-cell" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('updateCount')}>Updates{getSortIndicator('updateCount')}</th>
                                                            <th className="text-right d-sm-table-cell" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('deleteCount')}>Deletes{getSortIndicator('deleteCount')}</th>
                                                            <th className="text-right" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('totalCount')}>Total{getSortIndicator('totalCount')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {getSortedResources().map((resource) => (
                                                            <tr key={resource.resourceName}>
                                                                <td className="font-weight-bold" style={{ wordBreak: 'break-word' }}>{resource.resourceName}</td>
                                                                <td className="text-right">{resource.writeCount.toLocaleString()}</td>
                                                                <td className="text-right d-sm-table-cell">{resource.updateCount.toLocaleString()}</td>
                                                                <td className="text-right d-sm-table-cell">{resource.deleteCount.toLocaleString()}</td>
                                                                <td className="text-right font-weight-bold">{resource.totalCount.toLocaleString()}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Right Column: Resource Usage Bar Chart & Operation Trends */}
                                {usageByResource.length > 0 && (
                                    <div className="col-12 col-lg-6">
                                        <div class="ibox-content mt-0 no-top-border pb-0">
                                            <div className="row">
                                                <div className="col">
                                                    <h4>Usage by Resource</h4>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Resource Usage Bar Chart */}
                                        <div className="panel p-2 mb-2" id="usage-by-resource-chart">
                                            <div className="d-flex justify-content-end align-items-center mb-2">
                                                <div>
                                                    <button
                                                        onClick={() => setUsageByResourceChartType('bar')}
                                                        className={`btn btn-xs ${usageByResourceChartType === 'bar' ? 'btn-primary' : 'btn-white'
                                                            }`}
                                                    >
                                                        Bar
                                                    </button>
                                                    <button
                                                        onClick={() => setUsageByResourceChartType('line')}
                                                        className={`btn btn-xs ${usageByResourceChartType === 'line' ? 'btn-primary' : 'btn-white'
                                                            }`}
                                                    >
                                                        Line
                                                    </button>
                                                </div>
                                            </div>
                                            {usageByResourceChartType === 'bar' ? (
                                                <Bar
                                                    data={resourceBarChartData}
                                                    options={{
                                                        ...chartOptions,
                                                        scales: {
                                                            ...chartOptions.scales,
                                                            x: {
                                                                stacked: false,
                                                            },
                                                            y: {
                                                                stacked: false,
                                                                beginAtZero: true,
                                                            },
                                                        },
                                                    }}
                                                />
                                            ) : (
                                                <Line
                                                    data={resourceBarChartData}
                                                    options={{
                                                        ...chartOptions,
                                                        scales: {
                                                            ...chartOptions.scales,
                                                            x: {
                                                                stacked: false,
                                                            },
                                                            y: {
                                                                stacked: false,
                                                                beginAtZero: true,
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                        </div>
                                            <div class="ibox-content mt-0 no-top-border pb-0">
                                                <div className="row">
                                                    <div className="col">
                                                        <h4>Operation Trends Over Time</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Operation Trends Over Time */}
                                            <div className="panel p-2" id="usage-trends-chart">
                                                <div className="d-flex justify-content-end align-items-center mb-2">
                                                    <div>
                                                        <button
                                                            onClick={() => setOperationTrendChartType('bar')}
                                                            className={`btn btn-xs ${operationTrendChartType === 'bar' ? 'btn-primary' : 'btn-white'
                                                                }`}
                                                        >
                                                            Bar
                                                        </button>
                                                        <button
                                                            onClick={() => setOperationTrendChartType('line')}
                                                            className={`btn btn-xs ${operationTrendChartType === 'line' ? 'btn-primary' : 'btn-white'
                                                                }`}
                                                        >
                                                            Line
                                                        </button>
                                                    </div>
                                                </div>
                                                {operationTrendChartType === 'line' ? (
                                                    <Line data={chartData.lineChart} options={chartOptions} />
                                                ) : (
                                                    <Bar data={chartData.lineChart} options={chartOptions} />
                                                )}
                                            </div>
                                        </div>
                                )}
                                    </div>
                        </>
                    )}

                            {/* Empty State */}
                            {usageByResource.length === 0 && (
                                <div className="row">
                                    <div className="col-sm-12">
                                        <div className="alert alert-info mb-0">
                                            <p className="mb-0">No usage data available for the selected time period.</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
}

            export default ResourceUsage;
